import { shQuote } from './shellQuote';

/**
 * Server-side archiving: which tool to use, and the exact shell command.
 *
 * Pure, and its own module, because the command is what reaches a remote shell
 * and it is executed for real in tests — including against filenames built to
 * break it.
 *
 * # The tools, in order
 *
 * 1. **`zip`**, when installed. Fastest, and the classic.
 * 2. **`python3 -m zipfile`**, which writes a genuine `.zip` with nothing to
 *    install: Python 3 ships in the default Debian and Ubuntu images, which is
 *    what a fresh VPS almost always is. This is the case that fixed the owner's
 *    report — `bash: zip: command not found` on a new Hetzner box.
 * 3. **Neither** → the feature says so and tells the user how to install `zip`,
 *    rather than failing with a raw shell error.
 */

/**
 * One round trip that answers "what can this server archive with?". POSIX `sh`
 * only (`command -v`, not `which`, which is not guaranteed to exist and prints
 * differently across systems).
 */
export const ARCHIVE_PROBE =
  'if command -v zip >/dev/null 2>&1; then echo zip; ' +
  'elif command -v python3 >/dev/null 2>&1; then echo python3; ' +
  'else echo none; fi';

/** Map the probe's stdout to a tool. Anything unexpected is `none`: better to
 *  ask the user to install zip than to guess at a tool that may not be there. */
export function parseArchiveProbe(stdout) {
  const answer = String(stdout || '').trim().split(/\s+/).pop();
  return answer === 'zip' || answer === 'python3' ? answer : 'none';
}

export const isTarName = (name) => {
  const lower = String(name || '').toLowerCase();
  return lower.endsWith('.tar.gz') || lower.endsWith('.tgz');
};

/**
 * `./name`, so a name that STARTS WITH A DASH is a path and not an option.
 *
 * Not theoretical: `python3 -m zipfile -c out.zip -t` fails — argparse reads a
 * file called `-t` as `--test`. The usual fix, `--`, does not work there
 * (Python's `-c` takes a variable argument list), and it is not universal for
 * `zip` or BusyBox `tar` either. The prefix works for all three, so it is the
 * ONE mechanism rather than a mix of two.
 * The archive entries do NOT gain a `./`: zipfile stores each argument's
 * basename.
 */
const asPath = (name) => shQuote(`./${name}`);

/**
 * The full command: change into the directory, then archive the named entries.
 *
 * Every value — the directory, the archive name, each file — is single-quoted.
 * A file called `; rm -rf ~ #` is a legal name on a server.
 *
 * @param {{ dir: string, name: string, files: string[], tool: 'zip'|'python3' }} spec
 */
export function buildArchiveCommand({ dir, name, files, tool }) {
  const cd = `cd ${shQuote(dir)} &&`;
  const list = files.map(asPath).join(' ');
  if (isTarName(name)) {
    // tar is on essentially every Linux system, and a user who typed .tar.gz
    // asked for tar regardless of what zip tools exist.
    return `${cd} tar -czf ${asPath(name)} ${list}`;
  }
  if (tool === 'zip') {
    return `${cd} zip -r -q ${asPath(name)} ${list}`;
  }
  if (tool === 'python3') {
    return `${cd} python3 -m zipfile -c ${asPath(name)} ${list}`;
  }
  throw new Error(`no archive tool: ${tool}`);
}

/**
 * Swap whatever archive extension `name` has for `ext` (pass `''` to strip it),
 * so flipping the format in the Export dialog gives `site.tar.gz`, not
 * `site.zip.tar.gz`. Only a TRAILING archive extension is touched:
 * `my.backup.zip` keeps its `.backup`.
 */
export function withArchiveExt(name, ext) {
  const bare = String(name || '').replace(/\.(zip|tar\.gz|tgz)$/i, '');
  return `${bare}${ext}`;
}

